import { cloneDeep } from "lodash-es";
import type { Doc, Op } from "sharedb/lib/client";

import type { BlockType, FieldType, WorksheetType } from "../../../shared/types/worksheet";
import { BLOCKS_PATH, FIELDS_KEY, VALUE_KEY } from "../../../shared/worksheets/constants";
import { buildDeleteBlockOps, getUserData } from "../../../shared/worksheets/doc-helpers";
import { markdownToDelta } from "../../../worksheets/helpers/markdown-to-delta";
import { pseudoBlocksToWorksheetBlocks } from "../context/pseudo-blocks";
import type { AIWorksheetOperation } from "../schemas/worksheet-schema";

import { logInfoWithDebugFlag } from "utils/logger";

const sortOperationsForApply = (raw: AIWorksheetOperation[]): AIWorksheetOperation[] => {
  const deletes = raw.filter((o): o is Extract<AIWorksheetOperation, { type: "delete_block" }> => {
    return o.type === "delete_block";
  });
  const patches = raw.filter((o): o is Extract<AIWorksheetOperation, { type: "patch_field" }> => {
    return o.type === "patch_field";
  });
  const appends = raw.filter((o): o is Extract<AIWorksheetOperation, { type: "append" }> => {
    return o.type === "append";
  });
  return [...deletes, ...patches, ...appends];
};

const applyWorksheetOperation = (
  doc: Doc<WorksheetType>,
  operation: AIWorksheetOperation,
  blocks: WorksheetType["b"],
  ops: Op[],
): void => {
  logInfoWithDebugFlag("debug_ai_tools", "Applying operation:", operation);
  if (operation.type === "delete_block") {
    const blockIndex = blocks.findIndex((b) => b.id === operation.block_id);
    if (blockIndex < 0) {
      throw new Error(`delete_block: unknown block_id ${operation.block_id}`);
    }
    ops.push(...buildDeleteBlockOps(blocks, blockIndex, getUserData(doc)));
    blocks.splice(blockIndex, 1);
    return;
  }

  if (operation.type === "patch_field") {
    const hasText = operation.text_markdown !== undefined && operation.text_markdown !== null;
    const hasChoices = operation.choice_labels !== undefined && operation.choice_labels !== null;
    const hasCode = operation.code_source !== undefined && operation.code_source !== null;
    const patchKindCount = Number(hasText) + Number(hasChoices) + Number(hasCode);
    if (patchKindCount !== 1) {
      throw new Error(
        "patch_field: set exactly one of text_markdown, choice_labels, or code_source.",
      );
    }

    const blockIndex = blocks.findIndex((b) => b.id === operation.block_id);
    if (blockIndex < 0) {
      throw new Error(`patch_field: unknown block_id ${operation.block_id}`);
    }
    const fieldIndex = blocks[blockIndex].f.findIndex(
      (field: FieldType) => field.id === operation.field_id,
    );
    if (fieldIndex < 0) {
      throw new Error(
        `patch_field: unknown field_id "${operation.field_id}" on block ${operation.block_id}`,
      );
    }
    const field = blocks[blockIndex].f[fieldIndex];

    if (hasText) {
      if (field.t !== "Text") {
        throw new Error("patch_field: text_markdown is only valid for Text fields.");
      }
      const oldV = field.v;
      const md = String(operation.text_markdown).trim() || "\u00a0";
      const newV = markdownToDelta(md);
      ops.push({
        p: [BLOCKS_PATH, blockIndex, FIELDS_KEY, fieldIndex, VALUE_KEY],
        od: oldV,
        oi: newV,
      });
      field.v = newV;
      return;
    }

    if (hasCode) {
      if (field.t !== "CodeField") {
        throw new Error("patch_field: code_source is only valid for CodeField.");
      }
      const newC = String(operation.code_source);
      if (field.c !== newC) {
        ops.push({
          p: [BLOCKS_PATH, blockIndex, FIELDS_KEY, fieldIndex, "c"],
          od: field.c,
          oi: newC,
        });
        field.c = newC;
      }
      return;
    }

    if (field.t !== "ChoiceField") {
      throw new Error("patch_field: choice_labels is only valid for ChoiceField.");
    }
    const labels = operation.choice_labels!;
    if (labels.length !== field.c.length) {
      throw new Error(
        `patch_field: choice_labels length (${labels.length}) must match choices (${field.c.length}).`,
      );
    }
    labels.forEach((label, choiceIndex) => {
      const choice = field.c[choiceIndex];
      if (choice.l !== label) {
        ops.push({
          p: [BLOCKS_PATH, blockIndex, FIELDS_KEY, fieldIndex, "c", choiceIndex, "l"],
          od: choice.l,
          oi: label,
        });
        choice.l = label;
      }
    });
    return;
  }

  if (operation.type === "append") {
    if (!operation.blocks.length) {
      throw new Error("append: blocks must be non-empty.");
    }
    const newBlocks = pseudoBlocksToWorksheetBlocks(operation.blocks);
    if (!newBlocks.length) {
      throw new Error("append: no valid blocks produced from pseudo blocks.");
    }
    newBlocks.forEach((blk, i) => {
      ops.push({ p: [BLOCKS_PATH, blocks.length + i], li: blk });
    });
    blocks.push(...newBlocks);
  }
};
/**
 * Build ShareDB ops and the resulting block list after applying worksheet AI operations in-process.
 */
export const computeWorksheetModifyOperations = (
  doc: Doc<WorksheetType>,
  rawOperations: AIWorksheetOperation[],
): { finalBlocks: BlockType[]; ops: Op[] } => {
  if (!rawOperations.length) {
    throw new Error("No operations to apply.");
  }

  logInfoWithDebugFlag("debug_ai_tools", "entering computeWorksheetModifyOperations");

  const ordered = sortOperationsForApply(rawOperations);
  const blocks = cloneDeep(doc.data.b);
  const ops: Op[] = [];

  for (const operation of ordered) {
    applyWorksheetOperation(doc, operation, blocks, ops);
  }

  return { finalBlocks: blocks, ops };
};
